/**
 * Offer::Group hierarchy helpers (Claude-style numbering).
 *
 * Contract (API may add fields progressively — graceful fallbacks):
 *   { id, title, comment, row_order, parent_id, number, discipline, children? }
 *
 * Numbering convention:
 *   - top-level: N.0  (e.g. "1.0")
 *   - nested:    N.M / N.M.P
 */

export function groupDisplayTitle(group) {
  const title = (group?.title || group?.discipline || "").trim();
  return title || "";
}

/**
 * Format "Ventilasjon 1.0" — number after title when present.
 */
export function formatGroupLabel(group, fallbackTitle = "") {
  const title = groupDisplayTitle(group) || fallbackTitle;
  const number = (group?.number || "").toString().trim();
  if (number && title) return `${title} ${number}`;
  if (number) return number;
  return title;
}

/**
 * Client-side Claude numbering when API has not set `number` yet.
 * Mutates a shallow copy tree; does not overwrite existing numbers.
 */
export function assignDisplayNumbers(flatGroups = []) {
  const list = (flatGroups || []).map((g, index) => ({
    ...g,
    parent_id: g.parent_id ?? g.parentId ?? null,
    row_order:
      g.row_order !== undefined && g.row_order !== null ? g.row_order : index,
  }));

  const byParent = new Map();
  list.forEach((g) => {
    const key = g.parent_id == null || g.parent_id === "" ? "root" : String(g.parent_id);
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(g);
  });

  byParent.forEach((siblings) => {
    siblings.sort((a, b) => (a.row_order || 0) - (b.row_order || 0));
  });

  const result = new Map();

  const walk = (parentKey, parentNumber) => {
    const siblings = byParent.get(parentKey) || [];
    siblings.forEach((group, index) => {
      let number = (group.number || "").toString().trim();
      if (!number) {
        if (!parentNumber) {
          number = `${index + 1}.0`;
        } else {
          const base = parentNumber.endsWith(".0")
            ? parentNumber.slice(0, -2)
            : parentNumber;
          number = `${base}.${index + 1}`;
        }
      }
      const next = { ...group, number };
      result.set(String(group.id ?? group.clientId ?? `tmp-${index}`), next);
      const childKey = group.id != null ? String(group.id) : null;
      if (childKey) walk(childKey, number);
    });
  };

  walk("root", null);
  return list.map((g, index) => {
    const key = String(g.id ?? g.clientId ?? `tmp-${index}`);
    return result.get(key) || { ...g };
  });
}

/**
 * Flatten groups that may already include nested `children` from API as_json.
 * Dedupes by id so roots+children payloads do not double-count.
 */
export function flattenGroups(groupsOrTree = []) {
  const out = [];
  const seen = new Set();

  const walk = (nodes, parentId = null) => {
    (nodes || []).forEach((node, index) => {
      if (!node) return;
      const id = node.id != null ? String(node.id) : null;
      if (id && seen.has(id)) {
        // Still walk children in case this was a shallow stub
        if (Array.isArray(node.children) && node.children.length) {
          walk(node.children, node.id);
        }
        return;
      }
      if (id) seen.add(id);
      const { children, ...rest } = node;
      out.push({
        ...rest,
        parent_id:
          rest.parent_id !== undefined && rest.parent_id !== null
            ? rest.parent_id
            : parentId,
        row_order:
          rest.row_order !== undefined && rest.row_order !== null
            ? rest.row_order
            : index,
      });
      if (Array.isArray(children) && children.length) {
        walk(children, node.id ?? parentId);
      }
    });
  };

  walk(groupsOrTree, null);
  return out;
}

/**
 * Build nested tree from flat groups. Orphans (missing parent) become roots.
 */
export function buildGroupTree(flatGroups = []) {
  const flat = flattenGroups(flatGroups);
  const numbered = assignDisplayNumbers(flat);
  const nodes = numbered.map((g) => ({ ...g, children: [] }));
  const byId = new Map();
  nodes.forEach((n) => {
    if (n.id != null) byId.set(String(n.id), n);
  });

  const roots = [];
  nodes.forEach((n) => {
    const parentId = n.parent_id ?? n.parentId ?? null;
    if (parentId != null && byId.has(String(parentId))) {
      byId.get(String(parentId)).children.push(n);
    } else {
      roots.push(n);
    }
  });

  const sortRec = (arr) => {
    arr.sort((a, b) => (a.row_order || 0) - (b.row_order || 0));
    arr.forEach((n) => sortRec(n.children || []));
  };
  sortRec(roots);
  return roots;
}

/**
 * Suggest next top-level number from existing groups (e.g. "3.0").
 */
export function suggestNextTopLevelNumber(flatGroups = []) {
  const roots = (flatGroups || []).filter(
    (g) => g.parent_id == null || g.parent_id === "" || g.parent_id === undefined
  );
  let max = 0;
  roots.forEach((g) => {
    const n = String(g.number || "").match(/^(\d+)/);
    if (n) max = Math.max(max, parseInt(n[1], 10));
  });
  // Also count roots without numbers
  if (max === 0 && roots.length) max = roots.length;
  return `${max + 1}.0`;
}

/**
 * Suggest next child number under parent (e.g. parent "1.0" → "1.1").
 */
export function suggestNextChildNumber(flatGroups = [], parentId) {
  if (parentId == null) return suggestNextTopLevelNumber(flatGroups);
  const parent = (flatGroups || []).find((g) => String(g.id) === String(parentId));
  const parentNumber = (parent?.number || "").toString().trim();
  const base = parentNumber
    ? parentNumber.endsWith(".0")
      ? parentNumber.slice(0, -2)
      : parentNumber
    : "1";
  const children = (flatGroups || []).filter(
    (g) => String(g.parent_id ?? g.parentId) === String(parentId)
  );
  return `${base}.${children.length + 1}`;
}

/**
 * Return a Set of descendant group ids (not including `groupId` itself).
 */
export function descendantIds(flatGroups = [], groupId) {
  if (groupId == null || groupId === "") return new Set();
  const selfKey = String(groupId);
  const childrenByParent = new Map();
  (flatGroups || []).forEach((g) => {
    if (g?.id == null) return;
    const parentKey =
      g.parent_id == null || g.parent_id === ""
        ? null
        : String(g.parent_id);
    if (parentKey == null) return;
    if (!childrenByParent.has(parentKey)) childrenByParent.set(parentKey, []);
    childrenByParent.get(parentKey).push(String(g.id));
  });

  const out = new Set();
  const stack = [...(childrenByParent.get(selfKey) || [])];
  while (stack.length) {
    const id = stack.pop();
    if (out.has(id)) continue;
    out.add(id);
    const kids = childrenByParent.get(id);
    if (kids?.length) stack.push(...kids);
  }
  return out;
}

/**
 * Valid parent picker options for create/edit: root + all groups except
 * `excludeGroupId` and its descendants. Labels use Claude-style numbers.
 *
 * @returns {{ value: string|null, label: string, group?: object }[]}
 */
export function validParentOptions(flatGroups = [], excludeGroupId = null) {
  const numbered = assignDisplayNumbers(flattenGroups(flatGroups));
  const blocked = descendantIds(numbered, excludeGroupId);
  if (excludeGroupId != null && excludeGroupId !== "") {
    blocked.add(String(excludeGroupId));
  }

  const options = [{ value: null, labelKey: "offers.groups.parent_root" }];
  numbered.forEach((g) => {
    if (g?.id == null) return;
    if (blocked.has(String(g.id))) return;
    const label = formatGroupLabel(g, g.title || g.discipline || "");
    options.push({
      value: String(g.id),
      label: label || String(g.id),
      group: g,
    });
  });
  return options;
}

/**
 * Detect whether items/groups carry hierarchy fields from API.
 */
export function hasHierarchyFields(groupsOrItems = []) {
  return (groupsOrItems || []).some(
    (g) =>
      g?.parent_id != null ||
      g?.parentId != null ||
      (g?.number != null && String(g.number).trim() !== "") ||
      (g?.discipline != null && String(g.discipline).trim() !== "") ||
      (Array.isArray(g?.children) && g.children.length > 0)
  );
}

/**
 * Derive a display number for a flat customer-line group row from position
 * among sibling group rows when API number is absent.
 */
export function numberForFlatGroupRows(items = []) {
  let topIndex = 0;
  return (items || []).map((item) => {
    const isGroup =
      item?.item_type === "group" ||
      item?.type === "group" ||
      item?.group_row === true;
    if (!isGroup) return item;
    if (item.number != null && String(item.number).trim() !== "") return item;
    topIndex += 1;
    return { ...item, number: `${topIndex}.0` };
  });
}
